import { on } from '@ember/modifier';
import ReSelect from 'tio-reimagined/components/select';
import { service } from '@ember/service';
import { sortGradesByOrder } from '../../../../utils/sort-grades-by-order.ts';
import { trackedFunction } from 'reactiveweb/function';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
} from '../../../../utils/tuition-assistance/fields.ts';
import Component from '@glimmer/component';
import errorsForField from '../../../../helpers/errors-for-field.ts';
import ProgramFormField from '../../../../components/tuition-assistance/program-form-field.gts';
import type EnumModel from '../../../../models/enum.ts';
import type Store from '@ember-data/store';
import type TasProgramTemplateModel from '../../../../models/tas-program-template.ts';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type { TASProgramTemplateModelFieldsSignature } from '../../../../models/tas-program-template.ts';
import type { TASCourseModelFieldsSignature } from '../../../../models/tas-course.ts';

export interface TuitionAssistanceFormsCoursesGradeSelectOptionsSignature {
  Args: {
    programTemplate: TasProgramTemplateModel;
    updateCourseGrade: (value: string) => void;
    formValidationErrors: ValidationErrorSignature[];
    fieldsCopy: TASCourseModelFieldsSignature;
    renderInPlace?: boolean;
  };
}

export interface ValidationErrorSignature {
  errors: string[];
  value: string;
  path: string;
}

export default class TuitionAssistanceFormsCoursesGradeSelectOptions extends Component<TuitionAssistanceFormsCoursesGradeSelectOptionsSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: Store;

  get exclusiveGradeOptions() {
    return getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.args.programTemplate.fields
    ) as string[];
  }

  get defaultGradeOptions() {
    return (this.defaultGradeOptionsData.value || []).map((model: EnumModel) => model.name);
  }

  get isPreApprovalRoute() {
    const currentRouteName = this.router.currentRouteName;
    return !!(
      currentRouteName?.includes('pre-approval.add-course') ||
      currentRouteName?.includes('pre-approval.edit-course')
    );
  }

  get courseGradeHint() {
    return this.isPreApprovalRoute
      ? this.intl.t('common.tuition_assistance.course_details.course_grade_pre_approval_hint')
      : '';
  }

  get hideRequiredLabel() {
    return this.isPreApprovalRoute ? true : undefined;
  }

  defaultGradeOptionsData = trackedFunction(this, async () => {
    return (await this.store.query('enum', {
      filter: {
        flavor: 'AcademicPerformance',
      },
    })) as EnumModel[];
  });

  get gradeOptions() {
    return this.exclusiveGradeOptions.length
      ? sortGradesByOrder(this.exclusiveGradeOptions)
      : sortGradesByOrder(this.defaultGradeOptions);
  }

  selectedKeysForField = (name: keyof TASProgramTemplateModelFieldsSignature) => {
    return getAllValuesForTasField(
      name as keyof TASCourseModelFieldsSignature,
      this.args.fieldsCopy
    ) as string[];
  };

  <template>
    <ProgramFormField
      @name="COURSE_GRADE"
      @programTemplate={{@programTemplate}}
      @hideRequiredLabel={{this.hideRequiredLabel}}
      as |field|
    >
      {{#if field.isFieldOptionPicklist}}
        <ReSelect
          data-test={{field.name}}
          id={{field.inputId}}
          @items={{field.fieldOptions}}
          @selected={{getSingleValueForTasField field.name @fieldsCopy}}
          {{! @glint-expect-error: onChange typed as string|null but handler only accepts string }}
          @onChange={{@updateCourseGrade}}
          @clearable={{false}}
          @errors={{if
            field.isTouched
            (errorsForField field.name schemaErrors=@formValidationErrors)
          }}
          {{on "focusout" field.markTouched}}
          class="sm:max-w-72"
          @hint={{this.courseGradeHint}}
        />
      {{else}}
        <ReSelect
          data-test={{field.name}}
          id={{field.inputId}}
          @selected={{getSingleValueForTasField field.name @fieldsCopy}}
          @items={{this.gradeOptions}}
          {{! @glint-expect-error: onChange typed as string|null but handler only accepts string }}
          @onChange={{@updateCourseGrade}}
          @clearable={{false}}
          class="sm:max-w-72"
          @hint={{this.courseGradeHint}}
          @errors={{if
            field.isTouched
            (errorsForField field.name schemaErrors=@formValidationErrors)
          }}
          {{on "focusout" field.markTouched}}
        />
      {{/if}}
    </ProgramFormField>
  </template>
}
